import {ComponentConfigDefault, TimeConfigDefaultDefined} from "models/dataset-models";
import {ChronoUnitLegacy} from "utils/time-utils";

export function objectPropertiesToArray(object: any) {
    return Object.keys(object)
        .map((key) => {
            return {
                id: key,
                ...object[key]
            }
        });
}

export function getEasasRestUrl() {
    return window.contextRoot + "/rest";
}

export function pad(number: number) {
    let r = String(number);
    if (r.length === 1) {
        r = '0' + r;
    }
    return r;
}

export function getBuildId() {
    return process.env.BUILD_ID;
}

export function isProd() {
    return process.env.PRODUCTION === "true";
}

export function isDev() {
    return !isProd();
}

export const isNumeric = (num: any) => (typeof (num) === 'number' || typeof (num) === "string" && num.trim() !== '') && !isNaN(num as number);

export const isStringEmpty = (str: string | null | undefined) => !str

export const isStringBlank = (str: string | null | undefined) => (!str || str.trim().length === 0)

export const timeConfigValidation = (config: ComponentConfigDefault) => {
    let tempConfig: TimeConfigDefaultDefined = {
        alignStartTo: "HOURS" as ChronoUnitLegacy,
        startOffsetUnits: "HOURS" as ChronoUnitLegacy,
        startOffset: 0,
        lagUnits: "HOURS" as ChronoUnitLegacy,
        lagOffset: 0,
        leadUnits: "HOURS" as ChronoUnitLegacy,
        leadOffset: 0
    };

    if (config.alignStartTo) {
        tempConfig.alignStartTo = config.alignStartTo;
    }

    if (config.startOffsetUnits) {
        tempConfig.startOffsetUnits = config.startOffsetUnits;
    }
    if (config.startOffset) {
        tempConfig.startOffset = config.startOffset;
    }

    if (config.lagUnits) {
        tempConfig.lagUnits = config.lagUnits;
    }
    if (config.lagOffset) {
        tempConfig.lagOffset = config.lagOffset;
    }

    if (config.leadUnits) {
        tempConfig.leadUnits = config.leadUnits;
    }
    if (config.leadOffset) {
        tempConfig.leadOffset = config.leadOffset;
    }

    return tempConfig;
}

export const restApiRoot = getEasasRestUrl();

export function getInitials(name: string): string {
    if (name !== "") {
        const hasTokens = name.includes(' ')
        let firstLetter = name[0].toUpperCase()
        let secondLetter = hasTokens ?
            name.charAt(name.lastIndexOf(' ') + 1).toUpperCase() :
            name.length > 1 ? name[1] :
                ""

        return firstLetter + secondLetter
    }
    return ""
}


export function getColorForString(string: string): { color: string, bgColor: string } {
    const colors = [
        "#55efc4", "#81ecec", "#74b9ff", "#a29bfe", "#dfe6e9",
        "#00b894", "#00cec9", "#0984e3", "#6c5ce7", "#b2bec3",
        "#ffeaa7", "#fab1a0", "#ff7675", "#fd79a8",
        "#fdcb6e", "#e17055", "#d63031", "#e84393"
    ];

    const hash = Math.abs(hashCode(string));
    const bucket = hash % colors.length;

    return {color: "#ffffff", bgColor: colors[bucket]};
}

const hashCode = (str: string) => str.split('').reduce((s, c) => Math.imul(31, s) + c.charCodeAt(0) | 0, 0)

export const queryParam = (name: string) => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(name);
}

type Join<K, P> = K extends string | number ?
    P extends string | number ?
        `${K}${"" extends P ? "" : "."}${P}`
        : never : never;

type Prev = [never, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10,
    11, 12, 13, 14, 15, 16, 17, 18, 19, 20, ...0[]]

export type Paths<T, D extends number = 6> = [D] extends [never] ? never : T extends object ?
    {
        [K in keyof T]-?: K extends string | number ?
        `${K}` | Join<K, Paths<T[K], Prev[D]>>
        : never
    }[keyof T] : ""

export type Leaves<T, D extends number = 10> = [D] extends [never] ? never : T extends object ?
    { [K in keyof T]-?: Join<K, Leaves<T[K], Prev[D]>> }[keyof T] : "";


type DotPrefix<T extends string> = T extends "" ? "" : `.${T}`

type DotNestedKeys<T> = (T extends object ?
    {
        [K in Exclude<keyof T, symbol>]: `${K}${DotPrefix<DotNestedKeys<T[K]>>}`
    } [Exclude<keyof T, symbol>]
    : "") extends infer D ? Extract<D, string> : never;


export const setObjAttribute = (obj: any, path: string, value: any,) => {
    let i;
    let pathArr = path.split('.');
    for (i = 0; i < pathArr.length - 1; i++)
        obj = obj[pathArr[i]];

    obj[pathArr[i]] = value;
}

/**
 * Get attribute from object by path. If attribute is not found, or path is not valid, return default value
 * @param obj
 * @param path
 * @param rDefault
 */
export const getObjAttribute = (obj: any, path: any, rDefault: any = null) => {
    let i;
    let pathArr = path.split('.');
    for (i = 0; i < pathArr.length - 1; i++)
        obj = obj[pathArr[i]];

    return obj[pathArr[i]] || rDefault;
}

export const standardDeviation = (numArray: number[], averageCalculated?: number):number|undefined => {
    const arrLength = numArray.length;
    if(arrLength<=1) {
        return undefined
    }

    const mean = averageCalculated || numArray.reduce((s, n) => s + n) / arrLength;
    const variance = numArray.reduce((s, n) => s + (n - mean) ** 2, 0) / (arrLength - 1);
    return Math.sqrt(variance);
}

export const checkIfArraysLengthIsSame= (...arrays:Array<any>) => {
    const definedArrays = arrays.filter((arr) => arr !== undefined);

    if (definedArrays.length === 0) {
        return true; // All arrays are undefined, so they are considered to have the same length
    }

    const firstArrayLength = definedArrays[0].length;

    return definedArrays.every((arr) => arr.length === firstArrayLength);
}

export const extractNumber = (value: string | number | undefined): number => {
    if (!value) {
        return 0
    }
    if (typeof value === "string") {
        const parsedNumber = parseFloat(value);
        if (!isNaN(parsedNumber)) {
            return parsedNumber;
        } else {
            return 400;
        }
    }

    return value
}

export const stringArrayToObject = (array:string[]) => {
    const obj:any = {};
    array.forEach((key) => {
        obj[key] = undefined;
    });
    return obj;
}